import React, { useEffect, useState } from "react";
import HeaderFilter from "./widgets/HeaderFilter";
import PageTitle from "../../Components/layouts/header/page-title";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState, store } from "../../store/store";
import {
  setSnackBar,
  setToastMessage,
} from "../../features/ToastSlice/toastSlice";
import { Button } from "@heathmont/moon-core-tw";
import { getRequests, cancelTimeOffRequest } from "../../services/employee.service";
import InlineCalendar from "../../Components/UI/InlineCalendar";
import SimpleBar from "simplebar-react";
import { notificationSocket } from "../../utils/notification.socket.service";
import { apiBaseUrl } from "../../constants/environmentConstants/environmentConstants";

interface IRequestOff {
  leaves: IRequest[];
}
interface IRequest {
  monthName: string;
  leaves: ILeaves[];
}
interface ILeaves {
  date: string;
  _id: string;
  month: number;
  status?: string;
}

const STATUS_CLASSES: Record<string, string> = {
  Pending:  "bg-yellow-100 text-yellow-700",
  Applied:  "bg-blue-100 text-blue-700",
  Accepted: "bg-green-100 text-green-700",
  Rejected: "bg-red-100 text-red-700",
};

const TimeOff = () => {
  const { User } = useSelector((state: RootState) => state.auth);
  const currentYear = Number(moment().format("YYYY"));

  const [allLeaves, setAllLeaves] = useState<(ILeaves & { monthName: string })[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [month, setMonth] = useState<number>(moment().month() + 1);
  const [calendarYear, setCalendarYear] = useState<number>(currentYear);
  const [fetchAgain, setFetchAgain] = useState<boolean>(false);
  // year state kept only for HeaderFilter's drawer calendar (shows which days are already applied)
  const [year, setYear] = useState({
    name: moment().format("YYYY"),
    year: moment().format("YYYY"),
    label: "year",
  });
  const [cancelTarget, setCancelTarget] = useState<(ILeaves & { monthName: string }) | null>(null);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);

  const fetchRequest = async () => {
    setIsLoading(true);
    const years = [currentYear - 1, currentYear, currentYear + 1];
    const results = await Promise.all(years.map((y) => getRequests(y)));
    const flat: (ILeaves & { monthName: string })[] = [];
    results.forEach((res: any) => {
      res?.res?.leaves?.forEach((group: IRequest) => {
        group.leaves?.forEach((leaf: ILeaves) => {
          flat.push({ ...leaf, monthName: group.monthName });
        });
      });
    });
    // Furthest in the future first
    flat.sort(
      (a, b) =>
        moment(b.date, "MM/DD/YYYY").valueOf() -
        moment(a.date, "MM/DD/YYYY").valueOf()
    );
    setAllLeaves(flat);
    setIsLoading(false);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setIsCancelling(true);
    const res: any = await cancelTimeOffRequest(cancelTarget._id);
    setIsCancelling(false);
    setCancelTarget(null);
    if (res?.res) {
      store.dispatch(setSnackBar("success"));
      store.dispatch(setToastMessage("Time-off request cancelled"));
      setFetchAgain((prev) => !prev);
    } else {
      store.dispatch(setSnackBar("error"));
      store.dispatch(setToastMessage(res?.err?.message || "Failed to cancel request"));
    }
  };

  useEffect(() => {
    fetchRequest();
  }, [fetchAgain]);

  // Re-fetch when manager accepts or declines this nurse's request
  useEffect(() => {
    if (!User?.id || !apiBaseUrl) return;

    if (!notificationSocket.socket?.connected) {
      notificationSocket.initializeConnection(apiBaseUrl, User.id, User.role);
    }

    const handler = () => {
      console.log("[TimeOff] requestResponse received — refreshing");
      fetchRequest();
    };

    let retries = 0;
    const attach = () => {
      if (notificationSocket.socket) {
        notificationSocket.socket.off("requestResponse", handler);
        notificationSocket.socket.on("requestResponse", handler);
      } else if (retries < 10) {
        retries++;
        setTimeout(attach, 300);
      }
    };
    attach();

    return () => {
      notificationSocket?.socket?.off("requestResponse", handler);
    };
  }, [User?.id]);

  // Leaves matching the currently displayed calendar month+year
  const calendarLeaves = allLeaves.filter((leaf) => {
    const d = moment(leaf.date, "MM/DD/YYYY");
    return d.month() + 1 === month && d.year() === calendarYear;
  });

  const selectedMonthName = moment()
    .month(Number(month) - 1)
    .format("MMMM");

  return (
    <>
      <div className="bg-[#f8f8f8] min-h-screen">
        <PageTitle
          title="Time-off"
          subtitle="Apply and see past time-off requests"
        />
        <div className="flex flex-wrap pb-20 md:pb-0">
          {/* Left: calendar panel — hidden on mobile */}
          <div className="hidden md:block w-full md:w-1/4">
            <div className="border-r border-beerus bg-white shadow-[0_8px_24px_-6px_rgba(0,0,0,0.08)]">
              <div className="bg-white pt-[30px] 2xl:p-[20px] px-4">
                <h3 className="text-moon-20 font-semibold">Month Calendar</h3>
                <p className="text-gray-600 text-moon-14 mb-[15px]">
                  You are seeing the time off days for {selectedMonthName}
                </p>
                <div className="bg-gohan flex flex-col gap-2 rounded-moon-s-lg">
                  <SimpleBar className="overflow-y-auto h-[calc(100vh-285px)]">
                    <div className="flex-1">
                      <InlineCalendar
                        month={month}
                        year={calendarYear}
                        selectedDays={calendarLeaves}
                        setMonth={(m: number) => setMonth(m)}
                        setYear={(y: number) => setCalendarYear(y)}
                      />
                    </div>
                  </SimpleBar>
                </div>
              </div>
            </div>
          </div>

          {/* Right: flat table — same design as Leaves */}
          <div className="w-full md:w-3/4">
            <div className="p-[15px] md:p-[20px_30px]">
              {/* Year dropdown + Apply button */}
              <HeaderFilter
                yearDropDown={year}
                setYearDropDown={setYear}
                setFetchAgain={setFetchAgain}
                fetchAgain={fetchAgain}
              />

              <div className="mt-5">
                {isLoading ? (
                  <div className="flex justify-center items-center h-48 text-gray-400 text-moon-14">
                    Loading...
                  </div>
                ) : allLeaves.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <img
                      src={"images/no-schedule-to-approve.svg"}
                      alt="No time-off requests"
                      className="mb-4"
                    />
                    <p className="text-gray-400 text-moon-14">
                      No time-off requests found
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-moon-s-lg shadow-sm overflow-hidden">
                    {/* Table header */}
                    <div className="grid grid-cols-5 gap-4 px-6 py-3 bg-gray-50 border-b border-beerus text-moon-12 font-semibold text-gray-500 uppercase tracking-wide">
                      <div>Date</div>
                      <div>Day</div>
                      <div>Month</div>
                      <div>Status</div>
                      <div></div>
                    </div>

                    {/* Rows */}
                    <SimpleBar className="h-[calc(100vh-340px)] overflow-y-auto">
                      {allLeaves.map((leaf) => {
                        const d = moment(leaf.date, "MM/DD/YYYY");
                        const canCancel = leaf.status === "Pending" || leaf.status === "Applied";
                        return (
                          <div
                            key={leaf._id}
                            onClick={() => {
                              const d2 = moment(leaf.date, "MM/DD/YYYY");
                              setMonth(d2.month() + 1);
                              setCalendarYear(d2.year());
                            }}
                            className="grid grid-cols-5 gap-4 px-6 py-4 border-b border-beerus hover:bg-gray-50 transition-colors items-center cursor-pointer"
                          >
                            <div className="text-moon-14 font-medium text-blackcst">
                              {d.format("DD MMM YYYY")}
                            </div>
                            <div className="text-moon-14 text-gray-600">
                              {d.format("dddd")}
                            </div>
                            <div className="text-moon-14 text-gray-600">
                              {leaf.monthName}
                            </div>
                            <div>
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-moon-12 font-medium ${
                                  STATUS_CLASSES[leaf.status ?? ""] ??
                                  "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {leaf.status ?? "—"}
                              </span>
                            </div>
                            <div onClick={(e) => e.stopPropagation()}>
                              {canCancel && (
                                <button
                                  onClick={() => setCancelTarget(leaf)}
                                  className="text-moon-12 text-red-500 hover:text-red-700 hover:underline font-medium transition-colors"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </SimpleBar>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel confirmation modal */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-moon-s-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-moon-18 font-semibold text-gray-900 mb-2">
              Cancel Time-off Request
            </h3>
            <p className="text-moon-14 text-gray-600 mb-6">
              Are you sure you want to cancel your time-off request for{" "}
              <strong>
                {moment(cancelTarget.date, "MM/DD/YYYY").format("DD MMM YYYY")}
              </strong>?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setCancelTarget(null)}
                disabled={isCancelling}
              >
                Keep Request
              </Button>
              <button
                onClick={handleCancelConfirm}
                disabled={isCancelling}
                className="px-4 py-2 rounded-moon-s-sm bg-red-500 hover:bg-red-600 text-white text-moon-14 font-medium transition-colors disabled:opacity-50"
              >
                {isCancelling ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TimeOff;
